/**
 * Onglet Signature Energetique — Elements, Modes, Polarites
 * Reproduit pixel-perfect signature-energetique.php
 */

import { useState } from 'react';
import { colors } from '../../styles/tokens.js';
import { ELEMENT_COLORS } from './planetColors.js';
import type { ChartAnalysis } from '../../types/clients.js';

/** Couleurs des modes — identiques au PHP */
const MODE_COLORS: Record<string, string> = {
  'Cardinal': '#dc2626',
  'Fixe': '#2563eb',
  'Mutable': '#16a34a',
};

/** Couleurs des polarites */
const POL_COLORS: Record<string, string> = {
  'Yang': '#f59e0b',
  'Yin': '#8b5cf6',
};

/** Descriptions des elements dominants */
const DESC_ELEMENTS: Record<string, string> = {
  'Feu': 'Action, enthousiasme, energie creatrice, spontaneite',
  'Terre': 'Stabilite, sens pratique, construction, perseverance',
  'Air': 'Communication, intellect, echanges, adaptabilite',
  'Eau': 'Emotions, intuition, sensibilite, profondeur',
};

interface Props {
  signature: ChartAnalysis['signature'];
}

export function SignatureTab({ signature }: Props) {
  const [showHelp, setShowHelp] = useState(false);

  // Trouver la polarite dominante
  const polDominante = signature.polarites
    ? Object.entries(signature.polarites).sort((a, b) => b[1].percentage - a[1].percentage)[0]?.[0] ?? ''
    : '';

  return (
    <div style={{
      background: '#fff', borderRadius: 8, padding: '1.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      {/* Section header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1.25rem', paddingBottom: '0.75rem',
        borderBottom: `2px solid ${colors.border}`,
      }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', color: colors.text, fontWeight: 700 }}>
          <i className="bi bi-lightning-charge-fill" style={{ color: colors.accent, marginRight: 8 }} />
          Signature Energetique
        </h3>
        <button
          onClick={() => setShowHelp(!showHelp)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '5px 12px', fontSize: '0.8rem', fontWeight: 500,
            background: showHelp ? colors.accent : '#f3f4f6',
            color: showHelp ? '#fff' : colors.textMid,
            border: 'none', borderRadius: 6, cursor: 'pointer',
            transition: 'all 0.2s', fontFamily: 'inherit',
          }}
        >
          <i className="bi bi-question-circle" />
          Aide
        </button>
      </div>

      {/* Rubrique d'aide */}
      {showHelp && (
        <div style={{
          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
          padding: '1.25rem', marginBottom: '1.25rem', fontSize: '0.85rem',
          lineHeight: 1.7, color: '#1e3a5f',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0c4a6e' }}>
              Comprendre la Signature Energetique
            </h4>
            <button
              onClick={() => setShowHelp(false)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '1.2rem', color: '#64748b', lineHeight: 1,
              }}
            >
              &times;
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\uD83D\uDD25'} Les Elements
              </p>
              <p style={{ margin: '0 0 4px 0' }}>Decrivent le temperament fondamental :</p>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li><strong>Feu</strong> : Enthousiasme, action, creativite spontanee</li>
                <li><strong>Terre</strong> : Pragmatisme, stabilite, construction concrete</li>
                <li><strong>Air</strong> : Mental, communication, echanges intellectuels</li>
                <li><strong>Eau</strong> : Emotions, intuition, sensibilite profonde</li>
              </ul>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\u26A1'} Les Modes
              </p>
              <p style={{ margin: '0 0 4px 0' }}>Indiquent le mode d'action privilegie :</p>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li><strong>Cardinal</strong> : Initiative, leadership, demarrage</li>
                <li><strong>Fixe</strong> : Perseverance, concentration, stabilite</li>
                <li><strong>Mutable</strong> : Adaptabilite, flexibilite, transformation</li>
              </ul>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\u262F\uFE0F'} La Polarite
              </p>
              <p style={{ margin: '0 0 4px 0' }}>Revele l'orientation energetique :</p>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li><strong>Yang</strong> : Extraversion, action directe, affirmation</li>
                <li><strong>Yin</strong> : Interiorite, receptivite, reflexion</li>
              </ul>
            </div>

            <div>
              <p style={{ fontWeight: 700, marginBottom: 4 }}>
                {'\uD83D\uDCCA'} Systeme de points (32 pts)
              </p>
              <p style={{ margin: '0 0 4px 0' }}>Ponderation utilisee :</p>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li>Soleil, Lune, Ascendant = <strong>4 pts</strong> chacun</li>
                <li>Maitre de l'Ascendant = <strong>3 pts</strong></li>
                <li>Mercure, Venus, Mars = <strong>3 pts</strong> chacun</li>
                <li>Jupiter, Saturne = <strong>2 pts</strong> chacun</li>
                <li>Uranus, Neptune, Pluton = <strong>1 pt</strong> chacun</li>
                <li>Milieu du Ciel = <strong>1 pt</strong></li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

        {/* === Colonne gauche : Elements + Modes === */}
        <div>
          {/* Elements */}
          <div style={cardAnalysis}>
            <h4 style={cardTitle}>
              <span role="img">&#128293;</span> Repartition par Elements
            </h4>
            {Object.entries(signature.elements).map(([elem, data]) => {
              const isDominant = elem === signature.dominante.element;
              const barColor = ELEMENT_COLORS[elem] ?? '#999';
              return (
                <div key={elem} style={{ marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 4 }}>
                    <span style={{ fontWeight: isDominant ? 700 : 400 }}>
                      {elem} {isDominant ? '\u2605' : ''}
                    </span>
                    <span style={{ color: colors.textMid }}>
                      {data.weight}/32 pts ({Math.round(data.percentage)}%)
                    </span>
                  </div>
                  <div style={{ height: 10, background: '#e5e7eb', borderRadius: 5, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${data.percentage}%`,
                      background: barColor, borderRadius: 5,
                      transition: 'width 0.3s',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Modes */}
          <div style={cardAnalysis}>
            <h4 style={cardTitle}>
              <span role="img">&#9889;</span> Repartition par Modes
            </h4>
            {Object.entries(signature.modes).map(([mode, data]) => {
              const isDominant = mode === signature.dominante.mode;
              const barColor = MODE_COLORS[mode] ?? '#999';
              return (
                <div key={mode} style={{ marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 4 }}>
                    <span style={{ fontWeight: isDominant ? 700 : 400 }}>
                      {mode} {isDominant ? '\u2605' : ''}
                    </span>
                    <span style={{ color: colors.textMid }}>
                      {data.weight}/32 pts ({Math.round(data.percentage)}%)
                    </span>
                  </div>
                  <div style={{ height: 10, background: '#e5e7eb', borderRadius: 5, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${data.percentage}%`,
                      background: barColor, borderRadius: 5,
                      transition: 'width 0.3s',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* === Colonne droite : Polarite + Synthese === */}
        <div>
          {/* Polarites */}
          {signature.polarites && Object.keys(signature.polarites).length > 0 && (
            <div style={{ ...cardAnalysis, marginBottom: '1rem' }}>
              <h4 style={cardTitle}>
                <span role="img">&#9775;&#65039;</span> Polarite
              </h4>
              <div style={{ display: 'flex', gap: '1rem' }}>
                {Object.entries(signature.polarites).map(([pol, data]) => {
                  const isDominant = pol === polDominante;
                  const polColor = POL_COLORS[pol] ?? '#999';
                  return (
                    <div key={pol} style={{
                      flex: 1, textAlign: 'center', padding: '1rem',
                      background: isDominant ? (pol === 'Yang' ? '#fef3c7' : '#ede9fe') : '#f9fafb',
                      borderRadius: 10,
                      border: `3px solid ${isDominant ? polColor : 'transparent'}`,
                    }}>
                      <div style={{ fontSize: '2.5rem', marginBottom: '0.25rem' }}>
                        {pol === 'Yang' ? '\u2600\uFE0F' : '\uD83C\uDF19'}
                      </div>
                      <div style={{ fontWeight: isDominant ? 700 : 500, fontSize: '1.1rem' }}>
                        {pol}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: colors.textMid }}>
                        {data.count} pts ({Math.round(data.percentage)}%)
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Synthese — gradient dore */}
          <div style={{
            padding: '1.25rem',
            background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
            borderRadius: 12,
          }}>
            <div style={{ fontWeight: 700, color: '#92400e', marginBottom: '0.75rem', fontSize: '1rem' }}>
              <i className="bi bi-star-fill" style={{ marginRight: 6 }} />
              Signature dominante
            </div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#78350f', marginBottom: '0.5rem' }}>
              {signature.dominante.element} / {signature.dominante.mode}{polDominante ? ` / ${polDominante}` : ''}
            </div>
            <div style={{ fontSize: '0.9rem', color: '#92400e', lineHeight: 1.5 }}>
              {DESC_ELEMENTS[signature.dominante.element] ?? ''}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const cardAnalysis: React.CSSProperties = {
  background: colors.bgPage,
  borderRadius: 8,
  padding: '1.25rem',
  marginBottom: '1rem',
};

const cardTitle: React.CSSProperties = {
  margin: '0 0 1rem 0',
  fontSize: '0.95rem',
  color: colors.text,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};
